import React, { useState, useEffect, useMemo } from 'react';
import { getImageUrl } from '../lib/utils';

/**
 * InteractiveProductImage
 * - Cross-fades to the secondary product image on hover.
 * - Spawns animated floating olives + leaves drifting up & rotating.
 * - Subtle scale + parallax pulse on the main image.
 *
 * Props:
 *   product: { image, images?[], name }
 *   className?: string  -> applied to the outermost container
 *   imageClassName?: string -> applied to the <img> elements
 *   disableAnimation?: boolean
 */
const FLOATERS = [
  { left: '8%',  delay: '0s',   duration: '3.4s', size: 22, kind: 'olive' },
  { left: '22%', delay: '0.4s', duration: '4.2s', size: 16, kind: 'leaf' },
  { left: '38%', delay: '0.9s', duration: '3.8s', size: 24, kind: 'olive' },
  { left: '54%', delay: '0.2s', duration: '4.6s', size: 18, kind: 'leaf' },
  { left: '70%', delay: '0.7s', duration: '3.2s', size: 20, kind: 'olive' },
  { left: '84%', delay: '1.1s', duration: '4.0s', size: 16, kind: 'leaf' },
];

const OliveSvg = ({ size = 22 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
    <defs>
      <radialGradient id="oliveGrad" cx="35%" cy="30%" r="70%">
        <stop offset="0%" stopColor="#9CB07A" />
        <stop offset="60%" stopColor="#5F7041" />
        <stop offset="100%" stopColor="#3C4A28" />
      </radialGradient>
    </defs>
    <ellipse cx="16" cy="17" rx="9" ry="12" fill="url(#oliveGrad)" />
    <path d="M16 6 Q18 2 22 1" stroke="#6B7A3C" strokeWidth="1.4" fill="none" strokeLinecap="round" />
    <ellipse cx="20" cy="3" rx="2.4" ry="1.2" fill="#7C8F46" transform="rotate(28 20 3)" />
    <ellipse cx="14" cy="14" rx="1.6" ry="0.9" fill="#C3D29A" opacity="0.7" />
  </svg>
);

const LeafSvg = ({ size = 18 }) => (
  <svg viewBox="0 0 32 32" width={size} height={size} aria-hidden="true">
    <defs>
      <linearGradient id="leafGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#B8C98A" />
        <stop offset="100%" stopColor="#6B7A3C" />
      </linearGradient>
    </defs>
    <path
      d="M4 22 Q10 4 28 6 Q26 22 8 28 Q4 26 4 22 Z"
      fill="url(#leafGrad)"
    />
    <path d="M6 24 Q14 14 26 8" stroke="#4A5A28" strokeWidth="1" fill="none" strokeLinecap="round" />
  </svg>
);

const InteractiveProductImage = ({
  product,
  className = '',
  imageClassName = '',
  disableAnimation = false,
  children,
}) => {
  const [hover, setHover] = useState(false);

  // Pick a "back" image to cross-fade to.
  const backImage = useMemo(() => {
    const list = (product?.images || []).filter(Boolean);
    const main = product?.image;
    const alt = list.find((u) => u && u !== main);
    return alt || main || '';
  }, [product]);

  const frontImage = product?.image || (product?.images || [])[0] || '';

  // Render floaters only while hovered to avoid running animation when idle.
  // They mount when hover starts and unmount slightly after.
  const [showFloaters, setShowFloaters] = useState(false);
  useEffect(() => {
    if (hover) {
      setShowFloaters(true);
      return undefined;
    }
    // keep them mounted briefly for fade-out
    const t = setTimeout(() => setShowFloaters(false), 600);
    return () => clearTimeout(t);
  }, [hover]);

  return (
    <div
      className={`relative aspect-square overflow-hidden bg-white border ${className}`}
      onMouseEnter={() => !disableAnimation && setHover(true)}
      onMouseLeave={() => !disableAnimation && setHover(false)}
      data-testid="interactive-product-image"
    >
      {/* Front image */}
      <img
        src={getImageUrl(frontImage)}
        alt={product?.name || 'Ürün'}
        className={`absolute inset-0 w-full h-full object-contain transition-all duration-700 ease-out ${
          hover ? 'opacity-0 scale-110 rotate-1' : 'opacity-100 scale-100 rotate-0'
        } ${imageClassName}`}
        loading="eager"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'https://via.placeholder.com/400x400?text=VALMÉRA';
        }}
      />
      {/* Back image (visible on hover) */}
      {backImage && (
        <img
          src={getImageUrl(backImage)}
          alt={`${product?.name || 'Ürün'} alternatif görsel`}
          className={`absolute inset-0 w-full h-full object-contain transition-all duration-700 ease-out ${
            hover ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          } ${imageClassName}`}
          aria-hidden={!hover}
          loading="lazy"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}

      {/* Floating olives + leaves */}
      {showFloaters && (
        <div
          className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${
            hover ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden="true"
          data-testid="floating-olives"
        >
          {FLOATERS.map((f, i) => (
            <span
              key={i}
              className="absolute bottom-[-30px] valmera-float"
              style={{
                left: f.left,
                animationDelay: f.delay,
                animationDuration: f.duration,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.18))',
              }}
            >
              {f.kind === 'olive' ? <OliveSvg size={f.size} /> : <LeafSvg size={f.size} />}
            </span>
          ))}
        </div>
      )}

      {/* Subtle vignette on hover for depth */}
      <div
        className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${
          hover ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(95,112,65,0.10) 100%)' }}
      />

      {/* Slot for badges/overlays from parent */}
      {children}
    </div>
  );
};

export default InteractiveProductImage;
